import { useEffect, useState } from 'react';

import { useHashRoute } from '../../hooks/useHashRoute';
import {
  INSTRUCTIONS_BY_NAME,
  FETCHES_BY_NAME,
  EVENTS_BY_NAME,
  QUERIES_BY_NAME,
} from '../../data/instructions.generated';
import { useAdminMode } from '../../lib/admin-mode-context';
import { CommandPalette } from './CommandPalette';
import { EventPage } from './EventPage';
import { EventToasts } from './EventToasts';
import { EventsListenerPage } from './EventsListenerPage';
import { FetchPage } from './FetchPage';
import { InstructionPage } from './InstructionPage';
import { NetworkBar } from './NetworkBar';
import { QueryPage } from './QueryPage';
import { QuickstartPage } from './QuickstartPage';
import { Sidebar } from './Sidebar';
import { WalletButton } from './WalletButton';

export function PlaygroundShell() {
  const { segments, navigate } = useHashRoute();
  const { enabled: adminEnabled } = useAdminMode();
  const currentName = segments[0] ?? null;
  const isQuickstart = currentName === 'quickstart';
  const isEventsListener = currentName === 'events-listener';
  const isStaticPage = isQuickstart || isEventsListener;
  const currentIx = currentName && !isStaticPage ? INSTRUCTIONS_BY_NAME[currentName] : undefined;
  const currentFetch = currentName && !isStaticPage ? FETCHES_BY_NAME[currentName] : undefined;
  const currentEvent = currentName && !isStaticPage ? EVENTS_BY_NAME[currentName] : undefined;
  const currentQuery = currentName && !isStaticPage ? QUERIES_BY_NAME[currentName] : undefined;

  // ⌘K / Ctrl+K opens the command palette anywhere in the app. Captured at the
  // window level so input focus inside form fields doesn't swallow it.
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Route guards:
  //   - Default to Quickstart on first load or unknown route
  //   - Bounce off admin instructions when admin toggle is off
  useEffect(() => {
    if (!currentName) {
      navigate('quickstart');
      return;
    }
    if (!isStaticPage && !currentIx && !currentFetch && !currentEvent && !currentQuery) {
      navigate('quickstart');
      return;
    }
    if (currentIx?.isAdmin && !adminEnabled) {
      navigate('quickstart');
    }
  }, [
    currentName,
    isStaticPage,
    currentIx,
    currentFetch,
    currentEvent,
    currentQuery,
    adminEnabled,
    navigate,
  ]);

  return (
    <div className="shell" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <NetworkBar rightSlot={<WalletButton />} onOpenSearch={() => setPaletteOpen(true)} />
      <div className="body" style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar current={currentName} onNavigate={navigate} />
        <main className="main">
          <div className="main-inner">
            {isQuickstart && <QuickstartPage onPickInstruction={navigate} />}
            {isEventsListener && <EventsListenerPage />}
            {currentIx && <InstructionPage instruction={currentIx} />}
            {currentFetch && <FetchPage fetchEntry={currentFetch} />}
            {currentEvent && <EventPage eventEntry={currentEvent} />}
            {currentQuery && <QueryPage query={currentQuery} />}
          </div>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onPick={navigate} />
      <EventToasts />
    </div>
  );
}
